// Mirrors backend/invoice_calc.py exactly — keep both in sync.
export const num = (x) => { const n = Number(x); return Number.isFinite(n) ? n : 0; };
export const has = (v) => v !== null && v !== undefined && v !== "";
export function r2(x) {
  const v = num(x); const s = v < 0 ? -1 : 1;
  return (s * Math.round(Number((Math.abs(v) * 100).toFixed(6)))) / 100;
}
const pctAmount = (row, base) => (has(row.percent) ? r2(base * num(row.percent) / 100) : r2(num(row.amount)));

export function itemBase(l, mode = l.tp_mode || "before") {
  const price = num(l.price);
  return mode === "before" ? price : r2(price * (1 + num(l.tax_rate) / 100));
}

export function toItems(lines, showDiscount = true) {
  return lines.flatMap((l) => {
    const common = { product_id: l.product_id || null, name: l.name, hsn: l.hsn || "", unit: l.unit || "PCS", _key: l.key };
    if (l.is_free) return [{ ...common, qty: num(l.qty), price: 0, discount: 0, tax_rate: 0, is_free: true, note: l.note || "" }];
    const paid = { ...common, qty: num(l.qty), price: num(l.price), discount: showDiscount ? num(l.discount) : 0, tax_rate: num(l.tax_rate),
      note: l.note || "", tp_mode: l.tp_mode || "before",
      tp_rows: (l.tp_rows || []).filter((r) => r.narration || has(r.percent)).map((r) => ({ narration: r.narration || "", price: has(r.price) ? num(r.price) : "", percent: num(r.percent) })) };
    return num(l.free_qty) > 0 ? [paid, { ...common, qty: num(l.free_qty), price: 0, discount: 0, tax_rate: 0, is_free: true, note: "", _freeOf: l.key }] : [paid];
  });
}

export function computeInvoice(items, globalTp = [], discounts = [], charges = [], autoRound = false) {
  const out = []; const pre = [];
  items.forEach((it) => {
    const d = { ...it }; const qty = num(d.qty); const rate = num(d.tax_rate);
    if (d.is_free) { Object.assign(d, { price: 0, discount: 0, tax_rate: 0, tp_rows: [], final_price: 0, after_adj: 0, line_total: 0 }); out.push(d); pre.push(0); return; }
    const price = num(d.price); const net = Math.max(0, qty * price - num(d.discount));
    const mode = d.tp_mode || "before";
    const defBase = mode === "before" ? price : r2(price * (1 + rate / 100));
    let unitAdj = 0;
    const rows = (d.tp_rows || []).map((row) => {
      const base = has(row.price) ? num(row.price) : defBase; const adj = r2(base * num(row.percent) / 100);
      unitAdj += adj; return { ...row, base: r2(base), total: r2(base + adj), amount: r2(qty * adj) };
    });
    unitAdj = r2(unitAdj);
    let p, after;
    if (mode === "before") { p = r2(Math.max(0, net + qty * unitAdj)); after = 0; } else { p = r2(net); after = r2(qty * unitAdj); }
    Object.assign(d, { tp_mode: mode, tp_rows: rows, final_price: r2(price + unitAdj), after_adj: after, line_total: r2(p * (1 + rate / 100) + after) });
    out.push(d); pre.push(p);
  });
  const T = r2(pre.reduce((a, b) => a + b, 0));
  const gRows = []; let gBefore = 0; const gAfterRows = [];
  globalTp.forEach((row) => {
    if ((row.mode || "before") === "before") {
      const base = has(row.price) ? num(row.price) : T; const amt = pctAmount(row, base);
      gBefore += amt; gRows.push({ ...row, mode: "before", base: r2(base), amount: amt });
    } else gAfterRows.push(row);
  });
  const discRows = []; let beforeDisc = 0;
  discounts.forEach((dd) => { if (dd.mode !== "after") { const amt = pctAmount(dd, T); beforeDisc += amt; discRows.push({ ...dd, mode: "before", amount: amt }); } });
  const G = r2(gBefore - beforeDisc);
  const factor = T > 0 ? (T + G) / T : 1;
  let taxable = 0, tax = 0, itemAfter = 0;
  out.forEach((d, i) => {
    if (d.is_free) { d.taxable = 0; d.tax_amount = 0; return; }
    const tx = r2(Math.max(0, pre[i] * factor)); const t = r2(tx * num(d.tax_rate) / 100);
    d.taxable = tx; d.tax_amount = t; taxable += tx; tax += t; itemAfter += d.after_adj;
  });
  taxable = r2(taxable); tax = r2(tax); itemAfter = r2(itemAfter);
  const A = r2(taxable + tax + itemAfter);
  let gAfter = 0;
  gAfterRows.forEach((row) => { const base = has(row.price) ? num(row.price) : A; const amt = pctAmount(row, base); gAfter += amt; gRows.push({ ...row, mode: "after", base: r2(base), amount: amt }); });
  gAfter = r2(gAfter);
  const chRows = charges.map((c) => ({ ...c, amount: pctAmount(c, taxable) }));
  const chargesTotal = r2(chRows.reduce((a, c) => a + c.amount, 0));
  const B = r2(A + gAfter);
  let afterDisc = 0;
  discounts.forEach((dd) => { if (dd.mode === "after") { const amt = pctAmount(dd, B); afterDisc += amt; discRows.push({ ...dd, mode: "after", amount: amt }); } });
  afterDisc = r2(afterDisc);
  const raw = r2(Math.max(0, B + chargesTotal - afterDisc));
  const roundOff = autoRound ? r2(Math.round(raw + 1e-9) - raw) : 0;
  const grand = r2(raw + roundOff);
  return {
    items: out, tpRows: gRows, discRows, chRows,
    summary: { items_total: T, global_tp_before: r2(gBefore), before_disc: r2(beforeDisc), taxable_total: taxable, tax_total: tax,
      item_tp_after: itemAfter, global_tp_after: gAfter, charges_total: chargesTotal, after_disc: afterDisc, raw, round_off: roundOff,
      grand_total: grand, total_qty: r2(out.reduce((a, i) => a + num(i.qty), 0)) },
  };
}

// Rebuild editor lines from a saved sale/quotation; useCatalog refreshes price/tax from current products.
export function docToLines(doc, products, uid, useCatalog) {
  const lines = [];
  (doc.items || []).forEach((it) => {
    const prev = lines[lines.length - 1];
    if (it.is_free && prev && !prev.is_free && prev.product_id && prev.product_id === it.product_id && prev.free_qty == null) { prev.free_qty = it.qty; return; }
    const p = products.find((x) => x.id === it.product_id);
    const base = { key: uid(), product_id: p ? p.id : (it.product_id || null), name: it.name, hsn: it.hsn || p?.hsn || "", unit: it.unit || p?.unit || "PCS", qty: it.qty, note: it.note || null };
    if (it.is_free) { lines.push({ ...base, price: 0, discount: 0, tax_rate: 0, is_free: true }); return; }
    lines.push({ ...base, price: useCatalog && p ? p.selling_price : it.price, discount: it.discount || 0,
      tax_rate: useCatalog && p ? p.tax_rate : (it.tax_rate || 0), free_qty: null, tp_mode: it.tp_mode || "before",
      tp_rows: (it.tp_rows || []).map((r) => ({ narration: r.narration || "", price: has(r.price) ? r.price : "", percent: r.percent ?? "" })), tp_open: false });
  });
  return lines;
}
